import { useState, useEffect, useCallback, useRef } from "react";
import { useSelector, useDispatch } from "react-redux"; // Assuming Redux setup for auth
import { endpoint } from "../../api/endpoint";
import { apiHandler } from "../../api";

const DEBOUNCE_DELAY = 500; // ms

export const useOrders = () => {
  const dispatch = useDispatch();
  // Adjust these selectors based on your actual Redux state structure
  const { authData, storeId } = useSelector((state) => state.loginSlice);

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  const [filters, setFilters] = useState({
    order_status: "",
    txn_id: "",
    buyer_order_id: "",
    seller_order_id: "",
    buyer_app_id: "",
    seller_app_id: "",
    settlement_status: "",
    domain: "",
    order_created_at_from: "",
    order_created_at_to: "",
    search_by: "",
    search_value: "",
    sort_by: "order_created_at", // Default sorting column
    sort_order: "desc", // Default sort order
  });

  const [filterOptions, setFilterOptions] = useState({
    seller_app_ids: [],
    buyer_app_ids: [],
    order_statuses: [],
    settlement_statuses: [],
    domains: [],
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const debounceTimer = useRef(null);

  const getData = useCallback(async () => {
    try {
      setLoading(true);

      let url = endpoint.GET_ORDERS; // Assuming endpoint.GET_ORDERS is the base URL
      const params = new URLSearchParams();

      // Collect all filters to build URL parameters
      Object.entries(filters).forEach(([key, value]) => {
        if (value && value.toString().trim() !== "") {
          const cleanValue = value.toString().trim();

          // Handle generic search_by/search_value separately
          if (key === "search_value" || key === "search_by") return;

          params.append(key, cleanValue);
        }
      });

      // Handle generic search field
      if (filters.search_by && filters.search_value) {
        const searchVal = filters.search_value.toString().trim();
        // The API uses the filter key as the param name for specific searches
        params.append(filters.search_by, searchVal);
      }

      // Pagination parameters
      params.append("page", currentPage);
      params.append("limit", itemsPerPage);

      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const result = await apiHandler({
        url,
        method: "GET",
        headers: { "Content-Type": "application/json" },
        authToken: authData,
        dispatch,
        storeId,
      });

      // The key correction is HERE: accessing result.data.data
      const apiData = result?.data?.data;
      
      console.log("order result", apiData); // Log the correct object

      if (apiData?.rows) {
        setData(apiData.rows);
        setTotalCount(apiData.pagination?.total || 0);
      } else {
        setData([]);
        setTotalCount(0);
      }

      // Map API filter keys to local state keys
      if (apiData?.filters) {
        setFilterOptions({
          seller_app_ids: apiData.filters.seller_app_ids || [],
          buyer_app_ids: apiData.filters.buyer_app_ids || [],
          order_statuses: apiData.filters.order_statuses || [],
          settlement_statuses: apiData.filters.settlement_statuses || [],
          domains: apiData.filters.domains || [],
        });
      }

    } catch (err) {
      console.error("Error fetching order data:", err);
      setData([]);
      setTotalCount(0);
      setFilterOptions({
        seller_app_ids: [],
        buyer_app_ids: [],
        order_statuses: [],
        settlement_statuses: [],
        domains: [],
      });
    } finally {
      setLoading(false);
    }
    // Dependency array remains the same
  }, [filters, currentPage, itemsPerPage, authData, dispatch, storeId]);

  useEffect(() => {
    // Clear previous debounce timer
    if (debounceTimer.current) clearTimeout(debounceTimer.current);

    // Set a new debounce timer for search_value, other filters trigger immediately via dependency array
    debounceTimer.current = setTimeout(() => {
      getData();
    }, filters.search_value ? DEBOUNCE_DELAY : 0); // Apply debounce only when search value is being typed

    return () => clearTimeout(debounceTimer.current);
  }, [
    filters.order_status,
    filters.settlement_status,
    filters.domain,
    filters.seller_app_id,
    filters.buyer_app_id,
    filters.order_created_at_from,
    filters.order_created_at_to,
    filters.search_by, // Changing search_by should re-trigger
    filters.search_value, // Debounced
    filters.sort_order,
    filters.sort_by,
    currentPage,
    itemsPerPage,
    getData,
  ]);

  // Recalculate totalPages on totalCount or itemsPerPage change
  const totalPages = Math.ceil(totalCount / itemsPerPage);

  return {
    data,
    loading,
    filters,
    setFilters,
    filterOptions,
    totalCount,
    currentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage,
    totalPages,
  };
};